import { CellContext, ColumnDef } from '@tanstack/react-table'
import { Season, UpgradeTrack } from 'app/frontend/types/custom'
import { ColumnDefWithFilters, EnrichedTeamMembership } from '../types'
import { genericSorting } from 'app/frontend/base/config/column-definitions/util'
import { useMemo } from 'react'
import { rankToColor } from '../utils'
import { Medal } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAudit } from '../context'
import {
  colorByTrack,
  upgradeTracks,
} from 'app/frontend/types/custom/UpgradeTrack'
import { MultiSelect } from '@/components/ui/multi-select'
import AuditFilter, { useAuditFilterState } from '../components/AuditFilter'
import { usePageInfo } from 'app/frontend/base/config/queries'

export const valueToCutoff = (season: Season, value: number): UpgradeTrack => {
  const cutoff = season.track_cutoffs.find((cutoff) => value >= cutoff.ilvl)

  if (!cutoff) {
    return 'Adventurer'
  }

  switch (cutoff.difficulty) {
    case 'mythic':
      return 'Myth'
    case 'heroic':
      return 'Hero'
    case 'normal':
      return 'Champion'
    default:
      return 'Veteran'
  }
}

export const useVaultColumns = (): ColumnDefWithFilters => {
  const { visibleMembers, weeksAgo } = useAudit()
  const { value: enabledTracks, setValue: setEnabledTracks } =
    useAuditFilterState<UpgradeTrack[]>('vaultTracks', [], upgradeTracks)
  const {
    data: { currentSeason },
  } = usePageInfo()

  const getFilteredValue = (member: EnrichedTeamMembership, slot: number) => {
    const value =
      weeksAgo === 0
        ? member.data?.['great_vault_slot_' + slot]
        : member.data?.['historical_great_vault_slot_' + slot]?.split('|')[
            weeksAgo - 1
          ]

    if (!value || value === '-') {
      return undefined
    }

    const track = valueToCutoff(currentSeason, Number(value))
    if (enabledTracks.length > 0 && !enabledTracks.includes(track)) {
      return undefined
    }

    return value
  }

  const rankById = useMemo(() => {
    if (!visibleMembers.some((row) => row.data)) {
      return undefined
    }

    const withTotals = visibleMembers.map((row) => ({
      ...row,
      total: row.data
        ? Array.from({ length: 9 }).reduce(
            (acc: number, _, index) =>
              acc + (Number(getFilteredValue(row, index + 1) ?? '0') ?? 0),
            0,
          )
        : 0,
    }))

    const sorted = [...withTotals].sort((a, b) => b.total - a.total)

    return Object.fromEntries(
      withTotals.map((row) => {
        const rank = sorted.findIndex((r) => r.total === row.total) + 1
        return [row.id, rank]
      }),
    )
  }, [enabledTracks, visibleMembers, currentSeason, weeksAgo])

  const columns: ColumnDef<EnrichedTeamMembership>[] = useMemo(() => {
    return [
      {
        id: 'rank',
        size: 75,
        accessorFn: (row) => rankById?.[row.id.toString()] ?? 0,
        ...genericSorting<EnrichedTeamMembership>('alphanumeric', 'Rank'),
        meta: {
          className: 'border-r',
        },
        cell: (info) => {
          const value = rankById?.[info.row.original.id.toString()]
          return value ? (
            <div className={cn('flex items-center justify-center gap-2')}>
              <Medal className={cn('w-4 h-4', rankToColor(value))} /> {value}
            </div>
          ) : (
            ''
          )
        },
      },
      ...[
        {
          id: 'raid_group',
          header: 'Raid',
          slotOffset: 1,
          isLastGroup: false,
        },
        {
          id: 'dungeon_group',
          header: 'Dungeon',
          slotOffset: 4,
          isLastGroup: false,
        },
        {
          id: 'world_delves_group',
          header: 'World / Delves',
          slotOffset: 7,
          isLastGroup: true,
        },
      ].map(({ id, header, slotOffset, isLastGroup }) => ({
        id,
        header: () => <div className='text-center'>{header}</div>,
        meta: {
          className: !isLastGroup ? 'border-r' : '',
        },
        columns: Array.from({ length: 3 }).map((_, index) => {
          const slotNumber = slotOffset + index
          const isLastSlot = index === 2
          return {
            accessorFn: (row: EnrichedTeamMembership) =>
              getFilteredValue(row, slotNumber),
            ...genericSorting<EnrichedTeamMembership>(
              'alphanumeric',
              `Slot ${index + 1}`,
              true,
            ),
            id: `great_vault_slot_${slotNumber}`,
            meta: {
              className: isLastSlot && !isLastGroup ? 'border-r' : '',
            },
            cell: (info: CellContext<EnrichedTeamMembership, unknown>) => {
              const value = getFilteredValue(info.row.original, slotNumber)

              return value ? (
                <div
                  className={
                    colorByTrack[valueToCutoff(currentSeason, Number(value))]
                  }
                >
                  {value}
                </div>
              ) : (
                ''
              )
            },
          }
        }),
      })),
    ]
  }, [rankById, currentSeason, enabledTracks, visibleMembers, weeksAgo])

  const filters = useMemo(() => {
    return [
      <AuditFilter label='Counted tracks' key='track'>
        <MultiSelect<UpgradeTrack>
          options={upgradeTracks.slice(0, 4).map((track) => ({
            value: track,
            label: <span className={cn(colorByTrack[track])}>{track}</span>,
          }))}
          value={enabledTracks}
          placeholder='All'
          onChange={(value) => setEnabledTracks(value)}
        />
      </AuditFilter>,
    ]
  }, [enabledTracks])

  return {
    columns,
    filters,
    hasHistory: true,
    initialSortBy: [{ id: 'rank', desc: true }],
  }
}
